import {createFeatureSelector, createSelector} from '@ngrx/store';
import {generatePersonNameSelectorsFor, generateSelectorsDependentOn} from '@aviary/store/store.selector-generators';
import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {alwaysEmptyString} from '@aviary/store/common/common.selectors';
import {
  OPACRegistrationValue,
  REGISTRATION_CREATE_ACCOUNT_STEPS,
  REGISTRATION_FEATURE_KEY,
  REGISTRATION_LOOKUP_ACCOUNT_STEPS,
  RegistrationStep
} from '@store/registration/registration.types';
import {RegistrationState} from '@store/registration/registration.state';
import {FALLBACK_REGISTRATION_VALUE} from '@store/registration/registration.fallback';

const entireState: MxSelector<RegistrationState> = createFeatureSelector(REGISTRATION_FEATURE_KEY);

export namespace RegistrationSelectors {
  export namespace Registration {
    const subState: MxSelector<RegistrationState['registration']> = createSelector(
      entireState,
      state => state.registration
    );

    export const completedSteps: MxSelector<string[]> = createSelector(
      subState,
      _subState => _subState.completedSteps
    );

    export const isComplete: MxSelectorCreator<[RegistrationStep], boolean> =
      step => createSelector(
        completedSteps,
        (_completedSteps) => _completedSteps.includes(step)
      );

    export const verificationExpired: MxSelector<boolean> = createSelector(
      subState,
      _subState => _subState.verificationExpired
    );

    export const currentCreateAccountStep: MxSelector<number> = createSelector(
      completedSteps,
      (_completedSteps) => {
        for (const [index, item] of REGISTRATION_CREATE_ACCOUNT_STEPS.entries()) {
          if (!_completedSteps.includes(item)) {
            return index;
          }
        }
        return REGISTRATION_CREATE_ACCOUNT_STEPS.length - 1;
      }
    );

    export const currentLookupAccountStep: MxSelector<number> = createSelector(
      completedSteps,
      (_completedSteps) => {
        for (const [index, item] of REGISTRATION_LOOKUP_ACCOUNT_STEPS.entries()) {
          if (!_completedSteps.includes(item)) {
            return index;
          }
        }
        return REGISTRATION_LOOKUP_ACCOUNT_STEPS.length - 1;
      }
    );

    export const email: MxSelector<string> = createSelector(
      subState,
      _subState => _subState.email
    );

    export const cardNumber: MxSelector<string> = createSelector(
      subState,
      _subState => _subState.cardNumber
    );

    export const hasCardNumber: MxSelector<boolean> = createSelector(
      cardNumber,
      isPopulatedString
    );

    export const value: MxSelector<OPACRegistrationValue> = createSelector(
      subState,
      _subState => _subState.value
    );

    export const {
      verificationUUID,
      branchId,
      firstName,
      lastName,
      address,
      city,
      state,
      zip,
      dateOfBirth
    } = generateSelectorsDependentOn(value, FALLBACK_REGISTRATION_VALUE, NO_NAME_PREFIX);

    export const hasDateOfBirth: MxSelector<boolean> = createSelector(
      dateOfBirth,
      isPopulatedString
    );

    export const cityStateZip: MxSelector<string> = createSelector(
      city,
      state,
      zip,
      (_city, _state, _zip) =>
        `${_city}${isPopulatedString(_city) && isPopulatedString(_state) ? ', ' : ''}${_state} ${_zip}`.trim()
    );

    export const {
      fullName
    } = generatePersonNameSelectorsFor(firstName, alwaysEmptyString, lastName, alwaysEmptyString, NO_NAME_PREFIX);

  } // namespace Registration

  export namespace EmailValidation {
    const subState: MxSelector<RegistrationState['emailValidation']> = createSelector(
      entireState,
      state => state.emailValidation
    );

    export const isAvailable: MxSelector<boolean | 'UNKNOWN'> = createSelector(
      subState,
      _subState => _subState.isAvailable
    );
  } // namespace EmailValidation
}
